/**
 * Paso 2 de la carga: qué es cada columna del archivo (SPEC-BD §6.2).
 *
 * Una fila por columna, con 3 ejemplos (ya enmascarados por el backend) y el `select` «Qué es». Con
 * «Campo personalizado» aparecen el nombre del campo, el tipo y la clave con la que se guarda. Un campo
 * que ya existe en la marca no deja cambiar el tipo: el backend lo rechazaría (una columna de «Puntos»
 * que ayer era número no puede volver como texto).
 *
 * La lógica (qué destino, qué clave, qué error) vive en `logic/base.ts`: acá sólo se pinta.
 */

import { useId } from "react";
import { AlertTriangle } from "lucide-react";
import type { CampoCatalogo, ColumnaCarga, DestinoColumna, SugerenciaVinculo, TipoCampo, VinculoColumna } from "../../types.base";
import { DESTINOS, TIPOS_CAMPO, cambiarCampo, cambiarDestino, labelDestino } from "../../logic/base";
import { num, plural } from "../../../campana-nueva/utils/format";

interface Props {
  columnas: ColumnaCarga[];
  vinculo: VinculoColumna[];
  sugerencia: SugerenciaVinculo;
  catalogo: CampoCatalogo[];
  /** Errores junto al `select` de cada columna (`validarVinculoLocal(...).porColumna`). */
  errores: Record<string, string>;
  onChange: (v: VinculoColumna[]) => void;
  disabled?: boolean;
}

export function TablaVinculo({ columnas, vinculo, sugerencia, catalogo, errores, onChange, disabled = false }: Props) {
  const porColumna = new Map(columnas.map((c) => [c.columna, c]));
  const ambiguos = (sugerencia?.ambiguos || []).filter((a) => a.candidatos.length > 1);

  return (
    <div className="space-y-3">
      {ambiguos.map((a) => (
        <div key={a.destino} className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
          <span>
            Hay {a.candidatos.length === 2 ? "dos" : a.candidatos.length} columnas que pueden ser {labelDestino(a.destino)}: {a.candidatos.map((c) => `"${c}"`).join(", ")}. Elegí cuál es.
          </span>
        </div>
      ))}

      <div className="overflow-x-auto rounded-2xl border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Columna del archivo</th>
              <th className="px-4 py-2.5">Ejemplos</th>
              <th className="px-4 py-2.5 min-w-[16rem]">Qué es</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {vinculo.map((v) => (
              <FilaVinculo
                key={v.columna}
                v={v}
                col={porColumna.get(v.columna)}
                error={errores[v.columna] || ""}
                disabled={disabled}
                onDestino={(d) => onChange(cambiarDestino(vinculo, v.columna, d, { catalogo, sugerencia: sugerencia?.vinculo, titulo: tituloCampo(porColumna.get(v.columna)) }))}
                onCampo={(c) => onChange(cambiarCampo(vinculo, v.columna, c, catalogo))}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** El nombre por defecto de un campo nuevo: el encabezado, salvo que la columna viniera sin nombre. */
function tituloCampo(c: ColumnaCarga | undefined): string | undefined {
  if (!c) return undefined;
  return c.columna.startsWith("__EMPTY") ? "" : c.titulo || c.columna;
}

function FilaVinculo({ v, col, error, disabled, onDestino, onCampo }: {
  v: VinculoColumna; col: ColumnaCarga | undefined; error: string; disabled: boolean;
  onDestino: (d: DestinoColumna) => void; onCampo: (c: { etiqueta?: string; tipo?: TipoCampo }) => void;
}) {
  const idSelect = useId();
  const idCampo = useId();
  const idTipo = useId();
  const titulo = col?.titulo || v.columna;
  const ejemplos = (col?.ejemplos || []).filter((e) => String(e).trim()).slice(0, 3);

  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <label htmlFor={idSelect} className="font-semibold text-slate-900 break-words">{titulo}</label>
        {col && col.vacias > 0 && <p className="text-[11px] text-slate-400 mt-0.5">{num(col.vacias)} {plural(col.vacias, "vacía", "vacías")}</p>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {ejemplos.length ? (
          <ul className="space-y-0.5">{ejemplos.map((e, i) => <li key={i} className="truncate max-w-[14rem] font-mono">{e}</li>)}</ul>
        ) : <span className="text-slate-400">Sin datos</span>}
      </td>
      <td className="px-4 py-3 space-y-2">
        <select id={idSelect} value={v.destino} disabled={disabled} onChange={(e) => onDestino(e.target.value as DestinoColumna)}
          aria-invalid={!!error || undefined}
          className={`w-full h-9 rounded-xl border px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 ${error ? "border-red-300" : "border-slate-200"}`}>
          {DESTINOS.map((d) => (
            <option key={d.destino} value={d.destino}>{d.destino === "telefono" ? `${d.label} (obligatorio)` : d.label}</option>
          ))}
        </select>
        {error && <p className="text-xs text-red-600">{error}</p>}

        {v.destino === "campo" && v.campo && (
          <div className="rounded-xl bg-slate-50 p-3 space-y-2">
            <div>
              <label htmlFor={idCampo} className="text-[11px] font-semibold text-slate-600">Nombre del campo</label>
              <input id={idCampo} value={v.campo.etiqueta} maxLength={60} disabled={disabled}
                onChange={(e) => onCampo({ etiqueta: e.target.value })}
                className="w-full mt-1 h-8 rounded-lg border border-slate-200 px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label htmlFor={idTipo} className="text-[11px] font-semibold text-slate-600">Tipo</label>
              <select id={idTipo} value={v.campo.tipo} disabled={disabled || v.campo.existente}
                onChange={(e) => onCampo({ tipo: e.target.value as TipoCampo })}
                className="w-full mt-1 h-8 rounded-lg border border-slate-200 px-2 text-sm bg-white disabled:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500">
                {TIPOS_CAMPO.map((t) => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
              </select>
              {v.campo.existente && <p className="text-[11px] text-slate-500 mt-1">ya existe en tu base</p>}
            </div>
            {v.campo.clave && <p className="text-[11px] text-slate-400">se guarda como <code className="font-mono">{v.campo.clave}</code></p>}
          </div>
        )}
      </td>
    </tr>
  );
}

export default TablaVinculo;
